import React from 'react';
import { Flex } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import AIEmailTimer from './AIEmailTimer';
import { AI_EMAIL_TIMEOUT_MINUTES } from '@/common/constants';

interface ResolutionWindowProps {
  /**
   * UTC timestamp string when AI email was initiated
   */
  aiStartTime: string | null | undefined;
  /**
   * Total timeout duration in minutes (default: 60)
   */
  timeoutMinutes?: number;
  /**
   * Callback when cancel button (X) is clicked
   */
  onCancel?: () => void;
  /**
   * Whether to show the cancel button
   */
  showCancelButton?: boolean;
  /**
   * Custom label text (default: "Resolution window")
   */
  label?: string;
  /**
   * Display style: "inline" (label and timer on same line) or "stacked" (label above timer)
   */
  layout?: 'inline' | 'stacked';
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Callback when timer expires
   */
  onExpire?: () => void;
}

/**
 * ResolutionWindow Component
 *
 * Displays the resolution window countdown timer for AI email conversations.
 * Shows remaining time and optionally a cancel button.
 *
 * @param aiStartTime - UTC timestamp when AI email was initiated
 * @param timeoutMinutes - Total timeout duration (default: 60 minutes)
 * @param onCancel - Callback when X button is clicked
 * @param showCancelButton - Whether to show the cancel button
 * @param label - Custom label text
 * @param layout - Display layout style
 * @param className - Additional CSS class
 * @param onExpire - Callback when timer expires
 */
const ResolutionWindow: React.FC<ResolutionWindowProps> = ({
  aiStartTime,
  timeoutMinutes = AI_EMAIL_TIMEOUT_MINUTES,
  onCancel,
  showCancelButton = false,
  label = 'Resolution window',
  layout = 'inline',
  className,
  onExpire,
}) => {
  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    }
  };

  if (layout === 'stacked') {
    return (
      <Flex
        vertical
        gap={4}
        className={className}
        style={{ fontSize: '12px', color: 'var(--text-secondary)' }}
      >
        <Flex justify='space-between' align='center'>
          <span style={{ fontWeight: 400 }}>{label}</span>
          {showCancelButton && onCancel && (
            <CloseOutlined
              onClick={handleCancel}
              style={{
                cursor: 'pointer',
                fontSize: '14px',
                color: 'var(--text-primary)',
                padding: '4px',
              }}
            />
          )}
        </Flex>
        <AIEmailTimer
          aiStartTime={aiStartTime}
          timeoutMinutes={timeoutMinutes}
          format='short'
          onExpire={onExpire}
        />
      </Flex>
    );
  }

  // Inline layout (default)
  return (
    <Flex
      align='center'
      gap={8}
      className={className}
      style={{ fontSize: '12px', color: 'var(--text-secondary)' }}
    >
      <span style={{ fontWeight: 400 }}>{label}:</span>
      <AIEmailTimer
        aiStartTime={aiStartTime}
        timeoutMinutes={timeoutMinutes}
        format='short'
        onExpire={onExpire}
      />
      {showCancelButton && onCancel && (
        <CloseOutlined
          onClick={handleCancel}
          style={{
            cursor: 'pointer',
            fontSize: '14px',
            color: 'var(--text-primary)',
            marginLeft: 'auto',
            padding: '4px',
          }}
        />
      )}
    </Flex>
  );
};

export default ResolutionWindow;
